/**
 * @page TelefonieRegelnBuilder
 * @description #1463 — Anruf-Workflow Stage 2: Nicht-Dev-Builder für den aktiven
 * Wählplan (voip_dialplans #1462). 3 Klartext-Abschnitte (Empfang / Themen &
 * Routing / Direktanfrage MA) + Preset "Standard-Setup", KEIN Flowchart.
 * Speichern validiert serverseitig + schreibt den aktiven Plan in place —
 * die Engine liest pro Anruf frisch, Änderungen wirken sofort ohne Deploy.
 * @route /telefonie/regeln (eingebunden via TelefoniePage.TelefonieRegelnPage)
 * @roles modul 'telefonie' (lesen), 'telefonie' FULL (speichern)
 * @i18n telefonie.regeln.*
 * @endpoints GET/PUT /api/voip/dialplan, GET /api/voip/dialplan/preset, GET /api/voip/extensions
 * @since v2.692.0 (#1463)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  PhoneIncoming, GitBranch, UserCheck, Plus, Trash2, ChevronUp, ChevronDown,
  Loader2, Save, RotateCcw, AlertTriangle, CheckCircle2, Bot, Users, ArrowRight,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

const ACTION_TYPES = ['call_group', 'call_extension', 'transfer_employee', 'play_announcement', 'email_hint', 'create_case'];
const TEXT_ACTIONS = new Set(['play_announcement', 'email_hint']);
const PHONE_ACTIONS = new Set(['call_group', 'call_extension', 'transfer_employee']);

const inputCls = 'h-9 px-3 text-sm border border-[var(--border-card,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[#338edc]/30 w-full';
const selectCls = inputCls;
const textareaCls = 'px-3 py-2 text-sm border border-[var(--border-card,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[#338edc]/30 w-full resize-y';
const iconBtnCls = 'p-1.5 rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-tertiary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-30 transition-colors';

function SectionCard({ Icon, step, title, desc, children }) {
  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl">
      <div className="flex items-start gap-3 px-5 py-4 border-b border-[var(--border-card,#E5E7EB)]">
        <div className="w-9 h-9 rounded-lg bg-[#EBF4FD] flex items-center justify-center shrink-0">
          <Icon size={18} className="text-[#338edc]" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{step} — {title}</h2>
          <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{desc}</p>
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Segmented({ value, options, onChange, disabled }) {
  return (
    <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card,#E5E7EB)]">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.value)}
          className={`px-3 h-8 text-sm rounded-lg transition-colors ${
            value === o.value
              ? 'bg-white shadow-sm font-medium text-[var(--text-primary)]'
              : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Ziel-Select pro Aktions-Typ. Platzhalter-Werte aus #1462 (Gruppen-NAME als
// String) bleiben als eigene Option sichtbar, bis Manuel ein echtes Ziel mappt.
function TargetSelect({ action, targets, onChange }) {
  const { t } = useLanguage();
  if (!PHONE_ACTIONS.has(action.type)) return null;
  const val = action.target_id == null ? '' : String(action.target_id);

  let options = [];
  if (action.type === 'call_group') {
    options = (targets.groups || []).filter((g) => g.aktiv !== false).map((g) => ({ value: String(g.id), label: g.name }));
    if (val && !options.some((o) => o.value === val)) options.unshift({ value: val, label: `${val} (${t('telefonie.regeln.platzhalter')})` });
  } else if (action.type === 'transfer_employee') {
    options = (targets.users || []).map((u) => ({ value: String(u.id), label: u.name }));
    if (val && !options.some((o) => o.value === val)) options.unshift({ value: val, label: `#${val} (${t('telefonie.regeln.platzhalter')})` });
  } else {
    const withDw = [
      ...(targets.users || []).filter((u) => u.durchwahl != null).map((u) => ({ value: String(u.durchwahl), label: `${u.durchwahl} — ${u.name}` })),
      ...(targets.groups || []).filter((g) => g.durchwahl != null).map((g) => ({ value: String(g.durchwahl), label: `${g.durchwahl} — ${g.name}` })),
    ].sort((a, b) => a.value.localeCompare(b.value));
    options = withDw;
    if (val && !options.some((o) => o.value === val)) options.unshift({ value: val, label: `${val} (${t('telefonie.regeln.platzhalter')})` });
  }

  return (
    <select className={selectCls} value={val} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">{t('telefonie.regeln.zielFehlt')}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function ActionRow({ action, targets, onChange, onRemove, canRemove }) {
  const { t } = useLanguage();
  const set = (patch) => onChange({ ...action, ...patch });
  return (
    <div className="border border-[var(--border-card,#E5E7EB)] rounded-lg p-3 bg-[var(--bg-inset,#F8F9FC)]">
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] items-start">
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.regeln.aktion')}</label>
          <select
            className={selectCls}
            value={action.type}
            onChange={(e) => set({ type: e.target.value, target_id: null, params: {} })}
          >
            {ACTION_TYPES.map((ty) => <option key={ty} value={ty}>{t(`telefonie.regeln.aktionTyp.${ty}`)}</option>)}
          </select>
        </div>
        <div>
          {PHONE_ACTIONS.has(action.type) && (
            <>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.regeln.ziel')}</label>
              <TargetSelect action={action} targets={targets} onChange={(v) => set({ target_id: v })} />
            </>
          )}
          {PHONE_ACTIONS.has(action.type) && (
            <div className="mt-2">
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.regeln.onNoAnswer')}</label>
              <select
                className={selectCls}
                value={action.on_no_answer || 'next'}
                onChange={(e) => set({ on_no_answer: e.target.value === 'next' ? null : e.target.value })}
              >
                <option value="create_case">{t('telefonie.regeln.onNoAnswer.create_case')}</option>
                <option value="next">{t('telefonie.regeln.onNoAnswer.next')}</option>
              </select>
            </div>
          )}
          {TEXT_ACTIONS.has(action.type) && (
            <>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.regeln.ansageText')}</label>
              <textarea
                className={textareaCls}
                rows={3}
                maxLength={1000}
                value={action.params?.text || ''}
                onChange={(e) => set({ params: { ...action.params, text: e.target.value } })}
                placeholder={t('telefonie.regeln.ansageTextHint')}
              />
              {/* #1503-Nachschärfung 3 — Vorlese-Modus pro Ansage-Aktion. */}
              <label className="block text-xs font-medium text-[var(--text-secondary)] mt-2 mb-1">{t('telefonie.regeln.ansageModus')}</label>
              <select
                className={selectCls}
                value={action.params?.modus === 'woertlich' ? 'woertlich' : 'sinngemaess'}
                onChange={(e) => set({ params: { ...action.params, modus: e.target.value } })}
              >
                <option value="sinngemaess">{t('telefonie.regeln.ansageModus.sinngemaess')}</option>
                <option value="woertlich">{t('telefonie.regeln.ansageModus.woertlich')}</option>
              </select>
            </>
          )}
          {action.type === 'create_case' && (
            <p className="text-sm text-[var(--text-tertiary)] mt-1 sm:mt-6">{t('telefonie.regeln.createCaseInfo')}</p>
          )}
        </div>
        <button type="button" className={iconBtnCls} onClick={onRemove} disabled={!canRemove} title={t('common.delete')}>
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

function TopicCard({ topic, index, count, targets, onChange, onMove, onRemove }) {
  const { t } = useLanguage();
  const set = (patch) => onChange({ ...topic, ...patch });
  const setAction = (i, a) => set({ actions: topic.actions.map((x, j) => (j === i ? a : x)) });
  return (
    <div className="border border-[var(--border-card,#E5E7EB)] rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-[#EBF4FD] text-[#338edc] text-xs font-semibold flex items-center justify-center shrink-0">{index + 1}</span>
        <input
          className={inputCls}
          maxLength={100}
          value={topic.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder={t('telefonie.regeln.themaName')}
        />
        <button type="button" className={iconBtnCls} onClick={() => onMove(-1)} disabled={index === 0}><ChevronUp size={15} /></button>
        <button type="button" className={iconBtnCls} onClick={() => onMove(1)} disabled={index === count - 1}><ChevronDown size={15} /></button>
        <button type="button" className={`${iconBtnCls} hover:text-red-600`} onClick={onRemove}><Trash2 size={15} /></button>
      </div>
      <div>
        <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.regeln.aiHint')}</label>
        <textarea
          className={textareaCls}
          rows={2}
          maxLength={500}
          value={topic.ai_hint}
          onChange={(e) => set({ ai_hint: e.target.value })}
          placeholder={t('telefonie.regeln.aiHintPlaceholder')}
        />
      </div>
      <div className="space-y-2">
        <label className="block text-xs font-medium text-[var(--text-secondary)]">{t('telefonie.regeln.aktionsKette')}</label>
        {topic.actions.map((a, i) => (
          <ActionRow
            key={i}
            action={a}
            targets={targets}
            onChange={(na) => setAction(i, na)}
            onRemove={() => set({ actions: topic.actions.filter((_, j) => j !== i) })}
            canRemove={topic.actions.length > 1}
          />
        ))}
        {topic.actions.length < 5 && (
          <button
            type="button"
            onClick={() => set({ actions: [...topic.actions, { type: 'create_case', target_id: null, params: {}, on_no_answer: null }] })}
            className="inline-flex items-center gap-1.5 h-8 px-3 text-sm rounded-lg border border-dashed border-[var(--border-card,#E5E7EB)] text-[var(--text-tertiary)] hover:text-[#338edc] hover:border-[#338edc] transition-colors"
          >
            <Plus size={14} /> {t('telefonie.regeln.aktionHinzufuegen')}
          </button>
        )}
      </div>
    </div>
  );
}

export default function TelefonieRegelnBuilder() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [targets, setTargets] = useState({ users: [], groups: [] });
  const [planName, setPlanName] = useState(null);
  const [reception, setReception] = useState({ mode: 'ai_immediate', ring_target_type: null, ring_target_id: null, ring_seconds: 15 });
  const [topics, setTopics] = useState([]);
  const [direct, setDirect] = useState({ enabled: true, on_no_answer: 'case' });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // Gruppen-Platzhalter aus #1462 (Name-String) auf echte Gruppen-ID mappen,
  // wenn eine gleichnamige Gruppe existiert — AK4.
  const applyPlan = useCallback((plan, groupList) => {
    const byName = new Map((groupList || []).map((g) => [String(g.name).toLowerCase(), g.id]));
    const mapAction = (a) => {
      if (a.type === 'call_group' && a.target_id && !Number.isFinite(Number(a.target_id))) {
        const gid = byName.get(String(a.target_id).toLowerCase());
        if (gid) return { ...a, target_id: gid };
      }
      return a;
    };
    setReception({
      mode: plan.reception?.mode === 'ring_first' ? 'ring_first' : 'ai_immediate',
      ring_target_type: plan.reception?.ring_target_type === 'group' ? 'group' : 'all',
      ring_target_id: plan.reception?.ring_target_id ?? null,
      ring_seconds: plan.reception?.ring_seconds ?? 15,
    });
    setTopics((Array.isArray(plan.topics) ? plan.topics : []).map((tp) => ({
      id: tp.id,
      name: tp.name || '',
      ai_hint: tp.ai_hint || '',
      actions: (Array.isArray(tp.actions) && tp.actions.length ? tp.actions : [{ type: 'create_case', target_id: null, params: {}, on_no_answer: null }]).map(mapAction),
    })));
    setDirect({
      enabled: plan.direct_employee?.enabled !== false,
      on_no_answer: plan.direct_employee?.on_no_answer === 'substitute' ? 'substitute' : 'case',
    });
  }, []);

  useEffect(() => {
    let alive = true;
    Promise.all([
      fetch(`${API}/api/voip/dialplan`, { credentials: 'include' }).then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
      fetch(`${API}/api/voip/extensions`, { credentials: 'include' }).then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
    ])
      .then(([planRes, ext]) => {
        if (!alive) return;
        setTargets({ users: ext.users || [], groups: ext.groups || [] });
        if (planRes.plan) {
          setPlanName(planRes.plan.name);
          applyPlan(planRes.plan, ext.groups || []);
        }
        setLoading(false);
      })
      .catch((e) => { if (alive) { setLoadError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [applyPlan]);

  const loadPreset = async () => {
    try {
      const r = await fetch(`${API}/api/voip/dialplan/preset`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const preset = await r.json();
      applyPlan(preset, targets.groups);
      setDirty(true);
      setSaveError(null);
      setSavedAt(null);
    } catch (e) {
      setSaveError(e.message);
    }
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const r = await fetch(`${API}/api/voip/dialplan`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reception, topics, direct_employee: direct }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setPlanName(d.name || planName);
      setDirty(false);
      setSavedAt(new Date());
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const mut = (fn) => { fn(); setDirty(true); setSavedAt(null); };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--text-tertiary)] py-16 justify-center">
        <Loader2 size={16} className="animate-spin" /> {t('common.loading')}
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600 py-8 justify-center">
        <AlertTriangle size={16} /> {t('telefonie.regeln.loadError')}: {loadError}
      </div>
    );
  }

  const activeGroups = targets.groups.filter((g) => g.aktiv !== false);

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Kopf: Plan-Status + Preset + Speichern */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
          <GitBranch size={16} className="text-[#338edc]" />
          {planName
            ? <span>{t('telefonie.regeln.aktiverPlan')}: <span className="font-medium text-[var(--text-primary)]">{planName}</span></span>
            : <span>{t('telefonie.regeln.keinPlan')}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadPreset}
            disabled={saving}
            className="inline-flex items-center gap-1.5 h-9 px-3 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
          >
            <RotateCcw size={14} /> {t('telefonie.regeln.presetLaden')}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            className="inline-flex items-center gap-1.5 h-9 px-4 text-sm font-medium rounded-lg bg-[#338edc] text-white hover:bg-[#2b7bc4] disabled:opacity-40 transition-colors"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {t('telefonie.regeln.speichern')}
          </button>
        </div>
      </div>
      {saveError && (
        <div className="flex items-center gap-2 text-sm text-red-600 border border-red-200 bg-red-50 rounded-lg px-3 py-2">
          <AlertTriangle size={15} /> {saveError}
        </div>
      )}
      {savedAt && !dirty && (
        <div className="flex items-center gap-2 text-sm text-green-700 border border-green-200 bg-green-50 rounded-lg px-3 py-2">
          <CheckCircle2 size={15} /> {t('telefonie.regeln.gespeichert')}
        </div>
      )}

      {/* Read-only Fluss-Übersicht */}
      <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--text-secondary)] border border-[var(--border-card,#E5E7EB)] rounded-xl px-4 py-3 bg-[var(--bg-inset,#F8F9FC)]">
        <PhoneIncoming size={15} className="text-[#338edc]" />
        <span>{reception.mode === 'ring_first' ? t('telefonie.regeln.fluss.ringFirst') : t('telefonie.regeln.fluss.aiImmediate')}</span>
        <ArrowRight size={14} className="text-[var(--text-muted)]" />
        <Bot size={15} className="text-[#338edc]" />
        <span>{t('telefonie.regeln.fluss.themen', { count: topics.length })}</span>
        <ArrowRight size={14} className="text-[var(--text-muted)]" />
        <UserCheck size={15} className="text-[#338edc]" />
        <span>{direct.enabled ? t('telefonie.regeln.fluss.direktAn') : t('telefonie.regeln.fluss.direktAus')}</span>
      </div>

      {/* ① Empfang */}
      <SectionCard Icon={PhoneIncoming} step="①" title={t('telefonie.regeln.empfang')} desc={t('telefonie.regeln.empfangDesc')}>
        <div className="space-y-3">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="radio" name="reception" className="mt-1 accent-[#338edc]" checked={reception.mode === 'ai_immediate'}
              onChange={() => mut(() => setReception((r) => ({ ...r, mode: 'ai_immediate' })))} />
            <span className="text-sm text-[var(--text-primary)]">{t('telefonie.regeln.aiImmediate')}</span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="radio" name="reception" className="mt-1 accent-[#338edc]" checked={reception.mode === 'ring_first'}
              onChange={() => mut(() => setReception((r) => ({ ...r, mode: 'ring_first', ring_target_type: r.ring_target_type || 'all' })))} />
            <span className="text-sm text-[var(--text-primary)]">{t('telefonie.regeln.ringFirst')}</span>
          </label>
          {reception.mode === 'ring_first' && (
            <div className="grid gap-3 sm:grid-cols-2 pl-6 pt-1">
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.regeln.ringZiel')}</label>
                <select
                  className={selectCls}
                  value={reception.ring_target_type === 'group' && reception.ring_target_id ? `g_${reception.ring_target_id}` : 'all'}
                  onChange={(e) => mut(() => {
                    const v = e.target.value;
                    setReception((r) => v === 'all'
                      ? { ...r, ring_target_type: 'all', ring_target_id: null }
                      : { ...r, ring_target_type: 'group', ring_target_id: Number(v.slice(2)) });
                  })}
                >
                  <option value="all">{t('telefonie.regeln.ringZielAlle')}</option>
                  {activeGroups.map((g) => <option key={g.id} value={`g_${g.id}`}>{g.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.regeln.ringSekunden')}</label>
                <input
                  type="number" min={5} max={60} className={inputCls}
                  value={reception.ring_seconds ?? 15}
                  onChange={(e) => mut(() => setReception((r) => ({ ...r, ring_seconds: Number(e.target.value) })))}
                />
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      {/* ② Themen & Routing */}
      <SectionCard Icon={Bot} step="②" title={t('telefonie.regeln.themen')} desc={t('telefonie.regeln.themenDesc')}>
        <div className="space-y-4">
          {topics.length === 0 && (
            <p className="text-sm text-[var(--text-tertiary)]">{t('telefonie.regeln.keineThemen')}</p>
          )}
          {topics.map((tp, i) => (
            <TopicCard
              key={tp.id || i}
              topic={tp}
              index={i}
              count={topics.length}
              targets={targets}
              onChange={(nt) => mut(() => setTopics((arr) => arr.map((x, j) => (j === i ? nt : x))))}
              onMove={(dir) => mut(() => setTopics((arr) => {
                const j = i + dir;
                if (j < 0 || j >= arr.length) return arr;
                const copy = [...arr];
                [copy[i], copy[j]] = [copy[j], copy[i]];
                return copy;
              }))}
              onRemove={() => mut(() => setTopics((arr) => arr.filter((_, j) => j !== i)))}
            />
          ))}
          {topics.length < 20 && (
            <button
              type="button"
              onClick={() => mut(() => setTopics((arr) => [...arr, {
                id: '', name: '', ai_hint: '',
                actions: [{ type: 'create_case', target_id: null, params: {}, on_no_answer: null }],
              }]))}
              className="inline-flex items-center gap-1.5 h-9 px-3 text-sm rounded-lg border border-dashed border-[var(--border-card,#E5E7EB)] text-[var(--text-tertiary)] hover:text-[#338edc] hover:border-[#338edc] transition-colors"
            >
              <Plus size={15} /> {t('telefonie.regeln.themaHinzufuegen')}
            </button>
          )}
        </div>
      </SectionCard>

      {/* ③ Direktanfrage MA */}
      <SectionCard Icon={UserCheck} step="③" title={t('telefonie.regeln.direkt')} desc={t('telefonie.regeln.direktDesc')}>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-[var(--text-primary)]">{t('telefonie.regeln.direktToggle')}</span>
            <Segmented
              value={direct.enabled}
              onChange={(v) => mut(() => setDirect((d) => ({ ...d, enabled: v })))}
              options={[
                { value: true, label: t('telefonie.regeln.an') },
                { value: false, label: t('telefonie.regeln.aus') },
              ]}
            />
          </div>
          {direct.enabled && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-[var(--text-primary)]">{t('telefonie.regeln.direktFallback')}</span>
              <Segmented
                value={direct.on_no_answer}
                onChange={(v) => mut(() => setDirect((d) => ({ ...d, on_no_answer: v })))}
                options={[
                  { value: 'substitute', label: t('telefonie.regeln.direktFallback.substitute') },
                  { value: 'case', label: t('telefonie.regeln.direktFallback.case') },
                ]}
              />
            </div>
          )}
        </div>
      </SectionCard>

      <p className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
        <Users size={13} /> {t('telefonie.regeln.sofortHinweis')}
      </p>
    </div>
  );
}
